import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { Confirm } from '~/components/ui/Confirm';
import { useUpdateAssetMutation } from '~/generated/graphql';
import { Status } from '~/gql/graphql';
import { Asset } from '~/routes/assets';

type Props = {
  selected: string[];
  selectedAssets: Asset[];
  onCancel: () => void;
  onSuccess: () => void;
};

export const BulkInactivate = ({
  selected,
  selectedAssets,
  onCancel,
  onSuccess,
}: Props) => {
  const [, updateAsset] = useUpdateAssetMutation();
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);

    try {
      // Update each selected asset to inactive status
      const updatePromises = selectedAssets.map((asset) =>
        updateAsset({
          id: asset.id,
          input: {
            name: asset.name,
            type: asset.type || '',
            status: Status.Inactive,
            notes: asset.notes || '',
            images: asset.images || [],
            attachments: asset.attachments?.map((a) => a.id) || [],
            modelId: asset.model?.id
              ? typeof asset.model.id === 'string'
                ? parseInt(asset.model.id, 10)
                : Number(asset.model.id)
              : null,
          },
        })
      );

      const results = await Promise.all(updatePromises);

      // Check if any updates failed
      const errors = results.filter((r) => r.error);
      if (errors.length > 0) {
        console.error('Some assets failed to update:', errors);
        setIsLoading(false);
        toast.error(
          `Failed to update ${errors.length} of ${selectedAssets.length} asset${
            selectedAssets.length !== 1 ? 's' : ''
          }`
        );
        return;
      }

      // All updates succeeded
      onSuccess();
    } catch (error) {
      console.error('Failed to bulk update assets:', error);
      setIsLoading(false);
      toast.error('An unexpected error occurred while updating assets');
    }
  }, [selectedAssets, updateAsset, onSuccess]);

  return (
    <Confirm
      title='Make Assets Inactive'
      cancel='Cancel'
      confirm='Make Inactive'
      onCancel={onCancel}
      onConfirm={handleConfirm}
      isLoading={isLoading}
      danger
    >
      <div className='mb-3 text-center'>
        <p className='mb-2'>
          <strong>{selected.length}</strong> asset
          {selected.length !== 1 ? 's' : ''} will be made inactive.
        </p>
        <p className='text-sm text-grey-50'>
          Inactive assets will no longer appear in the default asset list.
        </p>
      </div>
      <p className='text-left'>Do you want to proceed?</p>
    </Confirm>
  );
};
